import React, { useEffect, useState } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { api, formatDetail } from "@/lib/api";
import { CheckCircle2, Sparkles, Gift } from "lucide-react";

const COUNTRIES = ["South Africa","Zimbabwe","Zambia","Botswana","Namibia","United Kingdom","United States","Canada","Australia","Germany","Other"];

export default function RegisterInterestPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [search] = useSearchParams();
  const refCode = (search.get("ref") || "").toUpperCase();
  const [form, setForm] = useState({
    full_name: "", email: "", phone: "",
    country_current: "", country_invest: "",
    interest_type: "home_owner",
    message: "", agreed_terms: false,
  });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.agreed_terms) { toast.error(t("form.must_agree", "Please agree to Terms & Policies.")); return; }
    setLoading(true);
    try {
      // Attach agent shortlink attribution (?ref=... or persisted localStorage 'mv_ref')
      let agentRef = "";
      try { agentRef = localStorage.getItem("mv_ref") || ""; } catch {}
      const urlRef = (search.get("ref") || "").toUpperCase();
      if (urlRef && urlRef.startsWith("MV-AGT-")) agentRef = urlRef;
      await api.post("/leads/register-interest", { ...form, ref_code: agentRef || "" });
      if (refCode && form.email) {
        try { await api.post("/referrals/apply", { code: refCode, email: form.email }); } catch {}
      }
      setDone(true);
      toast.success(t("form.registered_toast", "Registered. We will contact you within 2 working days."));
    } catch (err) {
      toast.error(formatDetail(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  if (done) {
    return (
      <div className="min-h-[70vh] max-w-3xl mx-auto px-4 md:px-8 py-24 text-center">
        <div className="w-16 h-16 rounded-full mx-auto flex items-center justify-center" style={{background:"rgba(212,175,55,0.2)"}}>
          <CheckCircle2 size={30} style={{color:"var(--mv-gold)"}} />
        </div>
        <h1 className="font-display text-3xl md:text-4xl mv-green-text mt-6">{t("form.thank_title")}</h1>
        <p className="mt-4 text-[15px]" style={{color:"var(--mv-muted)"}}>
          {t("form.thank_body")} <br/>
          <span className="font-accent italic text-lg" style={{color:"var(--mv-gold)"}}>{t("form.thank_signature", "MAVULA Investment — Open Doors.")}</span>
        </p>
        <div className="mt-8 flex justify-center gap-3">
          <Link to="/" className="btn-ghost">{t("form.back_home", "Back to Home")}</Link>
          <Link to="/pay?type=deposit" className="btn-gold" data-testid="thankyou-pay-deposit-btn">Pay Deposit Now →</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 md:px-8 py-16 md:py-20">
      <div className="mv-eyebrow"><span style={{color:"var(--mv-gold)"}}>{t("form.title_eyebrow")}</span></div>
      <h1 className="font-display text-3xl md:text-4xl lg:text-5xl mv-green-text mt-3 leading-tight">
        {t("form.title")} <span className="font-accent italic" style={{color:"var(--mv-gold)"}}>{t("form.title_italic")}</span>
      </h1>
      <p className="mt-4 text-[15px] max-w-2xl" style={{color:"var(--mv-muted)"}}>
        {t("form.sub")}
      </p>

      {refCode && (
        <div className="mt-6 mv-card p-4 flex items-center gap-3" style={{ background: "rgba(212,175,55,0.14)" }} data-testid="referral-banner">
          <Gift size={18} className="mv-green-text"/>
          <div className="text-[13.5px]">
            You were referred by <b className="mv-green-text">{refCode}</b>. Both accounts qualify for reduced admin fees.
          </div>
        </div>
      )}

      <div className="mt-6 mv-card p-4 flex items-start gap-3 border" style={{ background: "linear-gradient(180deg, #fff, rgba(233,60,60,0.05))", borderColor: "rgba(179,58,58,0.35)" }} data-testid="scam-banner">
        <span className="text-[13.5px]">
          <b style={{ color: "#B33A3A" }}>Scam warning:</b> MAVULA never asks for payment via WhatsApp, SMS or DMs.
          Only official bank transfers or the Pay page on the Mavula site. <a href="/scam-warning" className="underline font-semibold mv-green-text">Read the full warning →</a>
        </span>
      </div>

      <form onSubmit={submit} className="mt-10 mv-card p-6 md:p-8 grid grid-cols-1 md:grid-cols-2 gap-5" data-testid="register-interest-form">
        <Field label={t("form.full_name")} required>
          <input value={form.full_name} onChange={set("full_name")} required data-testid="ri-full-name" className={inputCls} placeholder="e.g. Thandiwe Moyo" />
        </Field>
        <Field label={t("form.email")} required>
          <input type="email" value={form.email} onChange={set("email")} required data-testid="ri-email" className={inputCls} placeholder="you@example.com" />
        </Field>
        <Field label={t("form.phone")} required>
          <input value={form.phone} onChange={set("phone")} required data-testid="ri-phone" className={inputCls} placeholder="+44 …" />
        </Field>
        <Field label={t("form.i_am")} required>
          <select value={form.interest_type} onChange={set("interest_type")} data-testid="ri-interest-type" className={inputCls}>
            <option value="home_owner">{t("form.home_owner")}</option>
            <option value="investor">{t("form.investor")}</option>
          </select>
        </Field>
        <Field label={t("form.country_current")} required>
          <select value={form.country_current} onChange={set("country_current")} required data-testid="ri-country-current" className={inputCls}>
            <option value="">{t("form.select")}</option>
            {COUNTRIES.map(c => <option key={c}>{c}</option>)}
          </select>
        </Field>
        <Field label={t("form.country_invest")} required>
          <select value={form.country_invest} onChange={set("country_invest")} required data-testid="ri-country-invest" className={inputCls}>
            <option value="">{t("form.select")}</option>
            {["South Africa","Zimbabwe","Zambia","Botswana","Namibia"].map(c => <option key={c}>{c}</option>)}
          </select>
        </Field>
        <div className="md:col-span-2">
          <Field label={t("form.message_optional")}>
            <textarea value={form.message} onChange={set("message")} data-testid="ri-message" rows={4} className={inputCls} placeholder={t("form.message_placeholder", "Tell us what you are looking for…")} />
          </Field>
        </div>
        <label className="md:col-span-2 flex items-start gap-3 text-[14px]" style={{color:"var(--mv-muted)"}}>
          <input type="checkbox" checked={form.agreed_terms} onChange={set("agreed_terms")} data-testid="ri-agree" className="mt-1 w-4 h-4 accent-[color:var(--mv-green)]" />
          <span>
            {t("form.agree_prefix", "I agree to the MAVULA")} <Link to="/policies" className="mv-green-text underline">{t("form.agree_link", "Terms & Policies")}</Link>{t("form.agree_suffix", ", and I confirm that I understand MAVULA Investment is a property scheme — not a bank, not a money transfer service.")}
          </span>
        </label>
        <div className="md:col-span-2 flex flex-wrap items-center justify-between gap-3 pt-2">
          <div className="text-[13px] flex items-center gap-2" style={{color:"var(--mv-muted)"}}>
            <Sparkles size={14} style={{color:"var(--mv-gold)"}}/> {t("form.reassurance", "No papers abroad required · confidential · dignified.")}
          </div>
          <button type="submit" disabled={loading} className="btn-gold disabled:opacity-60" data-testid="ri-submit">
            {loading ? t("form.sending") : t("form.submit")}
          </button>
        </div>
      </form>
    </div>
  );
}

const inputCls = "w-full px-4 py-2.5 rounded-xl border mv-hairline bg-white text-[14.5px] focus:outline-none";

function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="block text-[12.5px] uppercase tracking-[0.18em] mb-1.5" style={{color:"var(--mv-muted)"}}>
        {label} {required && <span style={{color:"var(--mv-gold)"}}>*</span>}
      </span>
      {children}
    </label>
  );
}
